/* import __COLOCATED_TEMPLATE__ from './conversation-explore-panel.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import { restartableTask } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import { type TaskGenerator } from 'ember-concurrency';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import { type Filter, type LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { type FlexibleQueryTimeRange } from 'embercom/services/reporting-csv-export';
import { tracked } from '@glimmer/tracking';
import type { Topic } from 'embercom/components/reporting/automation/ai-insights/fin-insights/topics-data-resources';
import type IntlService from 'ember-intl/services/intl';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import { isPresent } from '@ember/utils';
import type Range from 'embercom/models/reporting/range';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import type ReportingCsvExport from 'embercom/services/reporting-csv-export';
import { buildFiltersForDataConfig } from 'embercom/lib/reporting/custom/data-config-builder-helpers';
import type { ConversationExploreConversation } from 'embercom/components/reporting/automation/ai-insights/conversation-panel';
import {
  loadInsightsSummary,
  type InsightsSummaryResult,
} from 'embercom/components/reporting/automation/ai-insights/conversation-summary-data-resources';
import { type AttributeMapping } from 'embercom/services/reporting-underlying-data-service';
import type { TrendingTopic } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/topic-trends/trending-topics-data-resources';
import { convertMarkdownToHtml } from 'embercom/components/reporting/automation/ai-insights/optimize/summary-converter';

interface Args {
  showSidePanel: boolean;
  onClose: () => void;
  activeTab: Tab;
  selectedTopic: Topic | null;
  selectedSubTopic: string | null;
  isStandalone: boolean;
  dateRange: Range;
  filters?: LogicalFilter;
  onSelectSubTopic: (subtopic: string | null) => void;
  conversationCount: number;
  setConversationCount: (count: number) => void;
  trackAnalyticsEvent: (action: string, object: string, extraContext: Record<string, any>) => void;
  showSuggestions: boolean;
  loadSuggestions?: () => Promise<void>;
  trendingTopic?: TrendingTopic;
}

const CONVERSATION_PAGE_SIZE = 100;

type Tab = 'suggestions' | 'conversations' | 'trends';
type SummaryTab =
  | 'issue_summaries'
  | 'cxscore_drivers'
  | 'attribute_drivers'
  | 'resolution_rate_drivers';

export default class ConversationExplorePanel extends Component<Args> {
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare appService: AppService;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare intl: IntlService;
  @service declare reportingChartService: ReportingChartService;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare reportingCsvExport: ReportingCsvExport;

  @tracked totalHits = 0;
  @tracked pageFrom = 0;
  @tracked loadedConversations: ConversationExploreConversation[] = [];
  @tracked _selectedTab: Tab | null = null;
  @tracked selectedSummaryTab: SummaryTab = 'issue_summaries';
  @tracked selectedCxAttribute: string | undefined = undefined;
  @tracked selectedConversationId = 0;
  @tracked trendingTopicsAccordion: string | undefined = 'summary';

  fetchDataTask = taskFor(this.loadTopicDrillInData);

  conversationData = trackedTask(this, this.fetchDataTask, () => [
    this.args.selectedTopic,
    this.args.dateRange,
    this.pageFrom,
    this.args.selectedSubTopic,
  ]);

  fetchSummaryTask = taskFor(this.loadSummary);

  summaryData = trackedTask(this, this.fetchSummaryTask, () => [
    this.args.selectedTopic,
    this.args.dateRange,
    this.args.selectedSubTopic,
    this.selectedSummaryTab,
    this.selectedCxAttribute,
  ]);

  get selectedTab() {
    return this._selectedTab || this.args.activeTab;
  }

  set selectedTab(tab: Tab) {
    this._selectedTab = tab;
    this.args.trackAnalyticsEvent('tab-changed', 'conversation-explorer', {
      tab,
    });
  }

  get conversationTabLabel() {
    if (this.args.trendingTopic) {
      return this.intl.t('automation.ai-insights.conversation-explore-panel.sample-conversations', {
        count: this.args.conversationCount,
      });
    }

    return this.intl.t('automation.ai-insights.conversation-explore-panel.conversations', {
      count: this.args.conversationCount,
    });
  }

  get suggestionCount() {
    if (!this.args.selectedTopic) {
      return 0;
    }
    return this.contentSuggestionsService.getTopicsSuggestionCount(
      this.args.selectedTopic.name,
      this.args.selectedSubTopic,
    );
  }

  get filterState(): LogicalFilter {
    let filter: Filter[] = [
      {
        type: 'category',
        data: {
          attribute: 'conversation_custom_fields#ai_insights_topic',
          property: 'conversation_custom_fields#ai_insights_topic',
          values: [this.args.selectedTopic?.name],
        },
      },
    ];
    if (this.args.selectedSubTopic) {
      filter.push({
        type: 'category',
        data: {
          attribute: 'conversation_custom_fields#ai_insights_subtopic',
          property: 'conversation_custom_fields#ai_insights_subtopic',
          values: [this.args.selectedSubTopic],
        },
      });
    }
    if (this.selectedSummaryTab === 'attribute_drivers' && this.selectedCxAttribute) {
      filter.push({
        type: 'category',
        data: {
          attribute: 'conversation_custom_fields#customer_experience_attributes',
          property: 'conversation_custom_fields#customer_experience_attributes',
          values: [this.selectedCxAttribute],
        },
      });
    }
    if (this.selectedSummaryTab === 'cxscore_drivers') {
      filter.push({
        type: 'exists',
        data: {
          attribute: 'conversation.ai_generated_metrics.csat',
          property: 'ai_generated_metrics.csat',
        },
      });
    }
    if (isPresent(this.args.filters)) {
      filter.push(...this.args.filters!.filters);
    }

    if (isPresent(this.args.trendingTopic)) {
      // When opened on the topic trends page, we only want to show outlier conversations
      // so we reset the filter and only include the outlier conversation ids

      filter = [
        {
          type: 'category',
          data: {
            attribute: 'conversation.id',
            property: 'conversation_id',
            values: this.args.trendingTopic!.outlierConversationIds,
          },
        },
      ];
    }

    // ensure the filters are correctly transformed
    return buildFiltersForDataConfig(
      null, // renderableChart
      this.metric,
      null, // parentMetric
      { type: 'and', filters: filter },
      false, // includeReportLevelFilters
    ) as LogicalFilter;
  }

  get metric() {
    return this.reportingMetrics.getMetricById('v1.new_conversations');
  }

  get conversationFetchError() {
    return this.fetchDataTask.lastErrored?.error as any;
  }

  get timeRange(): FlexibleQueryTimeRange {
    return {
      start: this.args.dateRange.startMoment.valueOf(),
      end: this.args.dateRange.endMoment.valueOf(),
      interval: this.args.dateRange.interval,
      property: this.metric.timeProperty,
    };
  }

  get canExportFullConversations(): boolean {
    return this.appService.app.canUseFeature('reporting-drill-in-conversation-export');
  }

  get columnIds(): string[] {
    return [
      'conversation.id',
      'conversation.first_user.name',
      'conversation.first_user.pseudonym',
      'conversation.first_user.email',
      'conversation_custom_fields#ai_insights_conversation_summary',
      'conversation_custom_fields#ai_insights_subtopic',
      'conversation_custom_fields#ai_insights_topic',
      'conversation.ai_generated_metrics.csat_explanation',
      'conversation.ai_generated_metrics.csat',
      'conversation_custom_fields#customer_experience_attributes',
    ];
  }

  @action
  exportFullConversations() {
    this.reportingCsvExport.exportFullConversations(
      this.metric.datasetId,
      this.filterState,
      this.columnIds,
      this.timeRange,
      'conversation.first_user_conversation_part_created_at',
      'desc',
      this.appService.app.timezone,
    );
  }

  @restartableTask
  *loadTopicDrillInData(): TaskGenerator<void> {
    if (!this.args.selectedTopic || !this.args.dateRange) {
      return;
    }

    if (this.pageFrom === 0) {
      this.loadedConversations = [];
    }

    let filters: LogicalFilter = this.filterState;
    let timeRange = this.timeRange;
    let attributeIds = [
      'conversation.id',
      'conversation.first_user.name',
      'conversation.first_user.pseudonym',
      'conversation.first_user.email',
      'conversation_custom_fields#ai_insights_conversation_summary',
      'conversation_custom_fields#ai_insights_subtopic',
      'conversation_custom_fields#ai_insights_topic',
      'conversation.ai_generated_metrics.csat_explanation',
      'conversation.ai_generated_metrics.csat',
      'conversation_custom_fields#customer_experience_attributes',
    ];

    let sortBy = 'conversation.first_user_conversation_part_created_at';
    let sortDirection: 'asc' | 'desc' = 'desc';
    let syntheticDataUseCase = this.reportingChartService.syntheticDataUseCase;
    let isBeingRenderedAsAChart = false;
    let pageFrom = this.pageFrom;
    let pageSize = CONVERSATION_PAGE_SIZE;
    let result = yield this.reportingUnderlyingDataService.loadDrillInResult(
      this.appService.app.id,
      this.metric.datasetId,
      filters,
      pageFrom,
      pageSize,
      timeRange,
      attributeIds,
      sortBy,
      sortDirection,
      syntheticDataUseCase,
      this.appService.app.timezone,
      isBeingRenderedAsAChart,
    );
    if (isPresent(result.data) && this.pageFrom === 0) {
      this.selectedConversationId = result.data[0]['conversation.id'];
    }
    this.totalHits = result.totalHits;
    this.args.setConversationCount(result.totalHits);

    this.loadedConversations.pushObjects(
      result.data.map(
        (conversation: any) =>
          ({
            id: Number(conversation['conversation.id']),
            userName: conversation['conversation.first_user.name'],
            userPseudonym: conversation['conversation.first_user.pseudonym'],
            userEmail: conversation['conversation.first_user.email'],
            topicSummary:
              conversation['conversation_custom_fields#ai_insights_conversation_summary'],
            subtopic: conversation['conversation_custom_fields#ai_insights_subtopic'],
            topic: conversation['conversation_custom_fields#ai_insights_topic'],
            cxExplanation: conversation['conversation.ai_generated_metrics.csat_explanation'],
            cxScore: conversation['conversation.ai_generated_metrics.csat'],
            cxAttributes: conversation['conversation_custom_fields#customer_experience_attributes'],
          }) as ConversationExploreConversation,
      ),
    );
  }

  @restartableTask
  *loadSummary(): TaskGenerator<InsightsSummaryResult | null> {
    if (!this.appService.app.canSeeAiInsightsSummaries) {
      return null;
    }

    if (!this.args.dateRange) {
      return null;
    }

    if (this.selectedSummaryTab === 'attribute_drivers' && !this.selectedCxAttribute) {
      return null;
    }

    return yield loadInsightsSummary({
      appId: this.appService.app.id,
      adminId: this.appService.app.currentAdmin.id,
      filter: this.filterState,
      timeRange: this.timeRange,
      sortByAttributeId: 'conversation.first_user_conversation_part_created_at',
      sortDirection: 'desc',
      syntheticDataUseCase: this.reportingChartService.syntheticDataUseCase,
      timezone: this.appService.app.timezone,
      summaryType: this.selectedSummaryTab,
      selectedCxAttribute:
        this.selectedSummaryTab === 'attribute_drivers' ? this.selectedCxAttribute! : undefined,
    });
  }

  get summaryText(): string {
    if (this.selectedSummaryTab === 'attribute_drivers' && !this.selectedCxAttribute) {
      return '';
    }
    return this.fetchSummaryTask.lastSuccessful?.value?.summaryText || '';
  }

  get processedSummaryText(): string {
    let text = this.summaryText;
    if (!text) {
      return '';
    }

    return convertMarkdownToHtml(text, true, true);
  }

  get processedInsightExplanation(): string {
    let text = this.args.trendingTopic?.insightExplanation;
    if (!text) {
      return '';
    }

    return convertMarkdownToHtml(text, true, true);
  }

  get availableCxAttributes(): AttributeMapping {
    return this.reportingUnderlyingDataService.getAttributeKnownValues(
      'conversation_custom_fields#customer_experience_attributes',
    );
  }

  get cxAttributeOptions() {
    return Object.entries(this.availableCxAttributes).map(([value, text]) => ({
      value,
      text: text as string,
    }));
  }

  @action
  loadMoreConversations() {
    this.pageFrom += CONVERSATION_PAGE_SIZE;
    this.args.trackAnalyticsEvent('load-more-conversations', 'conversation-explorer', {
      pageFrom: this.pageFrom,
    });
  }

  @action
  onClose() {
    this.args.onClose();
    this.pageFrom = 0;
    this._selectedTab = null;
    this.trendingTopicsAccordion = 'summary';
  }

  @action
  onConversationSelected(conversationId: number) {
    this.args.trackAnalyticsEvent('conversation-card-click', 'conversation-explorer', {
      conversationId,
    });
    this.selectedConversationId = conversationId;
  }

  @action
  onConversationLinkClick(conversationId: number) {
    this._selectedTab = 'conversations';
    this.onConversationSelected(conversationId);
  }

  @action
  onSummaryTextClick(event: Event) {
    let target = event.target as HTMLElement;
    if (target.tagName === 'A' && target.dataset.conversationId) {
      event.preventDefault();
      let conversationId = parseInt(target.dataset.conversationId, 10);
      this.onConversationLinkClick(conversationId);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::ConversationExplorePanel': typeof ConversationExplorePanel;
    'reporting/automation/ai-insights/conversation-explore-panel': typeof ConversationExplorePanel;
  }
}
